import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  fetchGallery,
  uploadFile,
  deleteUpload,
  fetchGallerySliderKeys,
  toggleGallerySlider,
  fetchGalleryMasterItems,
  galleryImportUrl,
} from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import {
  Upload,
  Download,
  Loader2,
  Trash2,
  Copy,
  Check,
  ExternalLink,
  Search,
  RefreshCw,
  Image as ImageIcon,
  Film,
  Presentation,
  Mic,
  Play,
} from "lucide-react";

const MAX_MB = 50;

function humanSize(bytes) {
  if (!bytes) return "";
  const u = ["B", "KB", "MB", "GB"];
  let i = 0;
  let n = bytes;
  while (n >= 1024 && i < u.length - 1) {
    n /= 1024;
    i += 1;
  }
  return `${n.toFixed(n >= 10 || i === 0 ? 0 : 1)} ${u[i]}`;
}

// Bucket every file into one of the filter categories shown on orevital.com.
function bucketFor(item) {
  const m = item.media;
  if (m === "image") return "photos";
  if (m === "video") return "videos";
  if (m === "slides") return "powerpoint";
  if (m === "audio") return "voice";
  // Treat zip/pdf/doc/sheet/file as "other"; show them only under All.
  return "other";
}

const TABS = [
  { key: "all", label: "All", icon: null },
  { key: "photos", label: "Photos", icon: ImageIcon },
  { key: "videos", label: "Videos", icon: Film },
  { key: "powerpoint", label: "PowerPoint", icon: Presentation },
  { key: "voice", label: "Voice Samples", icon: Mic },
];

const BADGE_BY_BUCKET = {
  videos: { label: "VIDEO", cls: "bg-rose-500" },
  powerpoint: { label: "PPT", cls: "bg-orange-500" },
  voice: { label: "AUDIO", cls: "bg-emerald-500" },
};

export default function GalleryTab({ userRecordId }) {
  const [items, setItems] = useState([]);
  const [importUrl, setImportUrl] = useState("");
  const [importing, setImporting] = useState(false);
  async function importFromUrl() {
    const u = importUrl.trim();
    if (!u.startsWith("http")) { toast.error("Paste an image link or a page link first"); return; }
    setImporting(true);
    try {
      const r = await galleryImportUrl(userRecordId, u);
      toast.success(`Saved to your Gallery: ${(r.key || "").split("/").pop()}`);
      setImportUrl("");
      await refresh();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not import that link", { duration: 9000 });
    } finally {
      setImporting(false);
    }
  }
  const [loading, setLoading] = useState(true);
  const [configured, setConfigured] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [pending, setPending] = useState([]); // [{ file, name }] awaiting a name before upload
  const [progressText, setProgressText] = useState("");
  const [copiedKey, setCopiedKey] = useState(null);
  const [dragging, setDragging] = useState(false);
  const [pendingDelete, setPendingDelete] = useState(null);
  const [previewItem, setPreviewItem] = useState(null);
  const [search, setSearch] = useState("");
  const [sort, setSort] = useState("az"); // "az" | "za"
  const [tab, setTab] = useState("all");
  const [sliderUrls, setSliderUrls] = useState(() => new Set());
  const [canSlider, setCanSlider] = useState(false);
  const inputRef = useRef(null);

  const refresh = useCallback(async () => {
    if (!userRecordId) return;
    setLoading(true);
    let masterUrls = [];
    try {
      const res = await fetchGallery(userRecordId);
      let arr = res?.items || [];
      setConfigured(res?.configured !== false);
      // Merge the shared Master library (Airtable) so dealers also see the
      // brand's Master images/videos — not just their own uploads.
      try {
        const mres = await fetchGalleryMasterItems(userRecordId);
        const masters = mres?.items || [];
        if (masters.length) {
          masterUrls = masters.map((m) => m.url);
          const byUrl = new Map(arr.map((it) => [it.url, it]));
          const merged = [...arr];
          for (const m of masters) {
            const ex = byUrl.get(m.url);
            if (ex) {
              ex.master = true;
              ex.row_id = m.row_id;
            } else {
              merged.push(m);
              byUrl.set(m.url, m);
            }
          }
          arr = merged;
        }
      } catch {
        /* not admin/approved — show own uploads only */
      }
      setItems(arr);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not load gallery");
    } finally {
      setLoading(false);
    }
    // Admin + approved dealers can feature media on the home-page galleries.
    // Seed the checked-set with every Master url (incl. Airtable-hosted ones
    // whose Tiny Url isn't tracked by the keys endpoint).
    try {
      const sk = await fetchGallerySliderKeys(userRecordId);
      setSliderUrls(new Set([...(sk?.urls || []), ...masterUrls]));
      setCanSlider(true);
    } catch {
      setSliderUrls(new Set(masterUrls));
      setCanSlider(masterUrls.length > 0);
    }
  }, [userRecordId]);

  const toggleSlider = useCallback(
    async (item) => {
      const isOn = sliderUrls.has(item.url);
      // optimistic
      setSliderUrls((prev) => {
        const n = new Set(prev);
        if (isOn) n.delete(item.url);
        else n.add(item.url);
        return n;
      });
      try {
        const title = (item.filename || "").replace(/\.[^.]+$/, "");
        await toggleGallerySlider(userRecordId, item.url, title, !isOn, item.row_id);
        toast.success(isOn ? "Removed from home page" : "Added to home page");
      } catch (e) {
        // revert
        setSliderUrls((prev) => {
          const n = new Set(prev);
          if (isOn) n.add(item.url);
          else n.delete(item.url);
          return n;
        });
        toast.error(e?.response?.data?.detail || "Could not update");
      }
    },
    [sliderUrls, userRecordId]
  );

  useEffect(() => {
    refresh();
  }, [refresh]);

  // ----- Counts per filter ------------------------------------------------
  const counts = useMemo(() => {
    const c = { all: items.length, photos: 0, videos: 0, powerpoint: 0, voice: 0 };
    for (const it of items) {
      const b = bucketFor(it);
      if (b in c) c[b] += 1;
    }
    return c;
  }, [items]);

  // ----- Filter + search + sort ------------------------------------------
  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    let arr = items.filter((it) => {
      if (tab !== "all" && bucketFor(it) !== tab) return false;
      if (q && !(it.filename || "").toLowerCase().includes(q)) return false;
      return true;
    });
    arr = [...arr].sort((a, b) => {
      const an = (a.filename || "").toLowerCase();
      const bn = (b.filename || "").toLowerCase();
      return sort === "az" ? an.localeCompare(bn) : bn.localeCompare(an);
    });
    return arr;
  }, [items, tab, search, sort]);

  // ----- Upload ----------------------------------------------------------
  const cleanStem = (n) => (n || "").replace(/\.[^.]+$/, "").toLowerCase().replace(/[^a-z0-9._-]+/g, "-").replace(/^[-._]+|[-._]+$/g, "");
  const extOf = (n) => ((n || "").match(/\.([^.]+)$/) || [, ""])[1].toLowerCase();

  function askNames(files) {
    if (!files || !files.length) return;
    setPending(files.map((file) => ({ file, name: cleanStem(file.name) })));
  }

  async function uploadAll(entries) {
    if (!entries || !entries.length) return;
    setPending([]);
    setUploading(true);
    let ok = 0;
    try {
      for (let i = 0; i < entries.length; i += 1) {
        const { file, name } = entries[i];
        setProgressText(`Uploading ${i + 1} / ${entries.length} · ${name || file.name}`);
        if (file.size > MAX_MB * 1024 * 1024) {
          toast.error(`${file.name} is larger than ${MAX_MB} MB`);
          continue;
        }
        try {
          await uploadFile(file, userRecordId, "gallery", cleanStem(name) || undefined);
          ok += 1;
        } catch (e) {
          toast.error(
            `${file.name}: ${e?.response?.data?.detail || "upload failed"}`
          );
        }
      }
      if (ok) toast.success(`Uploaded ${ok} file${ok > 1 ? "s" : ""}`);
      if (ok) await refresh();
    } finally {
      setUploading(false);
      setProgressText("");
      if (inputRef.current) inputRef.current.value = "";
    }
  }

  function onPick(e) {
    const files = Array.from(e.target.files || []);
    if (files.length) askNames(files);
  }

  function onDrop(e) {
    e.preventDefault();
    setDragging(false);
    const files = Array.from(e.dataTransfer?.files || []);
    if (files.length) askNames(files);
  }

  async function copyUrl(url, key, e) {
    e?.stopPropagation?.();
    try {
      await navigator.clipboard.writeText(url);
      setCopiedKey(key);
      toast.success("URL copied");
      setTimeout(() => setCopiedKey((k) => (k === key ? null : k)), 1500);
    } catch {
      toast.error("Could not copy");
    }
  }

  async function confirmDelete(key) {
    setPendingDelete(null);
    try {
      await deleteUpload(userRecordId, key);
      setItems((arr) => arr.filter((it) => it.key !== key));
      toast.success("Deleted");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not delete");
    }
  }

  if (!configured) {
    return (
      <Card className="rounded-2xl border-zinc-200 p-6 text-center text-sm text-zinc-500">
        Cloudflare R2 is not configured on this server.
      </Card>
    );
  }

  return (
    <div className="space-y-4" data-testid="gallery-tab">
      <Dialog open={pending.length > 0} onOpenChange={(o) => { if (!o) { setPending([]); if (inputRef.current) inputRef.current.value = ""; } }}>
        <DialogContent className="max-w-md" data-testid="gallery-name-dialog">
          <DialogHeader>
            <DialogTitle>Name your {pending.length === 1 ? "file" : "files"}</DialogTitle>
          </DialogHeader>
          <p className="text-xs text-zinc-500 -mt-1">Saved as <code className="text-[11px]">…/postcue/&lt;name&gt;.ext</code> — letters, numbers and dashes only. Existing names get “-2”, “-3”.</p>
          <div className="space-y-2 max-h-72 overflow-y-auto">
            {pending.map((p, i) => (
              <div key={`${p.file.name}-${i}`} className="flex items-center gap-2">
                <Input
                  value={p.name}
                  onChange={(e) => setPending((arr) => arr.map((x, j) => (j === i ? { ...x, name: e.target.value } : x)))}
                  onKeyDown={(e) => { if (e.key === "Enter") uploadAll(pending); }}
                  className="h-9 text-sm"
                  autoFocus={i === 0}
                  data-testid={`gallery-name-input-${i}`}
                />
                <span className="text-xs text-zinc-500 shrink-0">.{extOf(p.file.name) || "file"}</span>
              </div>
            ))}
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => { setPending([]); if (inputRef.current) inputRef.current.value = ""; }} data-testid="gallery-name-cancel">Cancel</Button>
            <Button type="button" onClick={() => uploadAll(pending)} disabled={pending.some((p) => !cleanStem(p.name))} className="bg-indigo-600 hover:bg-indigo-700" data-testid="gallery-name-upload">
              Upload {pending.length > 1 ? `${pending.length} files` : ""}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {/* ============ Header row ============ */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-2">
          <span className="text-2xl">🖼️</span>
          <h2 className="text-lg sm:text-xl font-semibold text-zinc-900">
            Image Gallery
          </h2>
          <span className="text-sm text-zinc-500">
            ({counts.all.toLocaleString()} files)
          </span>
        </div>

        <div className="flex items-center gap-2">
          <input
            ref={inputRef}
            type="file"
            multiple
            className="hidden"
            onChange={onPick}
            data-testid="gallery-file-input"
          />
          <Button
            type="button"
            variant="outline"
            onClick={refresh}
            disabled={loading || uploading}
            className="h-10 w-10 rounded-lg p-0 border-zinc-200 bg-indigo-50 hover:bg-indigo-100"
            aria-label="Refresh"
            data-testid="gallery-refresh-btn"
          >
            <RefreshCw
              size={16}
              className={`text-indigo-700 ${loading ? "animate-spin" : ""}`}
            />
          </Button>
          <Button
            type="button"
            onClick={() => inputRef.current?.click()}
            disabled={uploading}
            className="h-10 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white font-semibold"
            data-testid="gallery-upload-btn"
          >
            {uploading ? (
              <Loader2 className="animate-spin mr-1.5" size={16} />
            ) : (
              <Upload size={16} className="mr-1.5" />
            )}
            Upload
          </Button>
        </div>
      </div>
      <div className="flex items-center gap-2 rounded-xl border border-dashed border-violet-200 bg-violet-50/40 p-2 mt-2" data-testid="gallery-import-box">
        <Download size={15} className="text-violet-600 shrink-0 ml-1" />
        <Input
          value={importUrl}
          onChange={(e) => setImportUrl(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter") importFromUrl(); }}
          placeholder="Save from link — paste an image address or a web page (Instagram: right-click photo → Copy image address)"
          className="h-9 rounded-lg text-sm bg-white"
          data-testid="gallery-import-url"
        />
        <Button type="button" onClick={importFromUrl} disabled={importing || !importUrl.trim()}
          className="h-9 rounded-lg bg-violet-600 hover:bg-violet-700 text-white shrink-0"
          data-testid="gallery-import-btn">
          {importing ? <Loader2 className="animate-spin" size={14} /> : "Save to Gallery"}
        </Button>
      </div>

      {uploading && (
        <div
          className="text-xs text-zinc-500 -mt-1"
          data-testid="gallery-upload-progress"
        >
          {progressText || "Uploading…"}
        </div>
      )}

      {/* ============ Search + sort ============ */}
      <div className="flex items-stretch gap-2">
        <div className="relative flex-1">
          <Search
            size={16}
            className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400"
          />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="🔎 Search by filename..."
            className="h-11 rounded-lg pl-9 bg-white border-zinc-200"
            data-testid="gallery-search"
          />
        </div>
        <div
          className="flex items-stretch rounded-lg overflow-hidden border border-zinc-200 bg-white"
          data-testid="gallery-sort"
        >
          <button
            type="button"
            onClick={() => setSort("az")}
            className={`px-3 text-xs font-bold transition-colors ${
              sort === "az"
                ? "bg-indigo-500 text-white"
                : "text-zinc-700 hover:bg-zinc-50"
            }`}
            data-testid="gallery-sort-az"
          >
            A→Z
          </button>
          <button
            type="button"
            onClick={() => setSort("za")}
            className={`px-3 text-xs font-bold transition-colors ${
              sort === "za"
                ? "bg-indigo-500 text-white"
                : "text-zinc-700 hover:bg-zinc-50"
            }`}
            data-testid="gallery-sort-za"
          >
            Z→A
          </button>
        </div>
      </div>

      {/* ============ Filter pills ============ */}
      <div className="flex items-center gap-2 overflow-x-auto -mx-1 px-1 pb-1">
        {TABS.map((t) => {
          const Icon = t.icon;
          const active = tab === t.key;
          const n = counts[t.key];
          // Hide empty non-"all" tabs except when active so the row stays clean.
          if (t.key !== "all" && !n && !active) return null;
          return (
            <button
              key={t.key}
              type="button"
              onClick={() => setTab(t.key)}
              className={`shrink-0 inline-flex items-center gap-1.5 px-3 h-9 rounded-lg text-xs font-semibold transition-colors ${
                active
                  ? "bg-indigo-500 text-white shadow-sm"
                  : "bg-indigo-50 text-zinc-700 hover:bg-indigo-100"
              }`}
              data-testid={`gallery-tab-${t.key}`}
            >
              {Icon ? <Icon size={13} /> : null}
              {t.label}
              {n !== undefined && (
                <span
                  className={`text-[10px] font-bold ${
                    active ? "opacity-90" : "opacity-70"
                  }`}
                >
                  ({n})
                </span>
              )}
            </button>
          );
        })}
      </div>

      {/* ============ Drop hint when empty ============ */}
      {!loading && items.length === 0 && (
        <Card
          onDragOver={(e) => {
            e.preventDefault();
            setDragging(true);
          }}
          onDragLeave={() => setDragging(false)}
          onDrop={onDrop}
          className={`rounded-2xl border-2 border-dashed p-10 text-center transition-colors ${
            dragging
              ? "border-emerald-500 bg-emerald-50"
              : "border-zinc-300 bg-zinc-50"
          }`}
          data-testid="gallery-drop-zone"
        >
          <div className="flex flex-col items-center gap-2">
            <Upload size={28} className="text-zinc-400" />
            <div className="text-sm font-semibold text-zinc-800">
              Drag &amp; drop, or click Upload
            </div>
            <div className="text-[11px] text-zinc-500">
              Images, video, audio, PDF, PowerPoint · up to {MAX_MB} MB each
            </div>
          </div>
        </Card>
      )}

      {/* ============ Grid ============ */}
      {loading ? (
        <div className="py-10 flex items-center justify-center text-zinc-500 text-sm">
          <Loader2 className="animate-spin mr-2" size={16} /> Loading…
        </div>
      ) : items.length > 0 && filtered.length === 0 ? (
        <Card className="rounded-2xl border-zinc-200 p-8 text-center text-sm text-zinc-500">
          No files match this filter.
        </Card>
      ) : (
        <div
          className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3"
          onDragOver={(e) => {
            e.preventDefault();
            setDragging(true);
          }}
          onDragLeave={() => setDragging(false)}
          onDrop={onDrop}
          data-testid="gallery-grid"
        >
          {filtered.map((it) => (
            <GalleryCard
              key={it.key}
              item={it}
              copied={copiedKey === it.key}
              onCopy={(e) => copyUrl(it.url, it.key, e)}
              onAskDelete={(e) => {
                e?.stopPropagation?.();
                setPendingDelete(it.key);
              }}
              onOpen={() => setPreviewItem(it)}
              canSlider={canSlider && (it.media === "image" || it.media === "video")}
              inSlider={sliderUrls.has(it.url)}
              onToggleSlider={(e) => {
                e?.stopPropagation?.();
                toggleSlider(it);
              }}
            />
          ))}
        </div>
      )}

      {/* ============ Preview lightbox ============ */}
      {previewItem && (
        <PreviewLightbox
          item={previewItem}
          onClose={() => setPreviewItem(null)}
          onCopy={(e) => copyUrl(previewItem.url, previewItem.key, e)}
          copied={copiedKey === previewItem.key}
        />
      )}

      {/* ============ Delete confirm ============ */}
      {pendingDelete && (
        <div
          className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm flex items-end sm:items-center justify-center px-4 py-6"
          onClick={() => setPendingDelete(null)}
          data-testid="gallery-delete-modal"
        >
          <div
            className="w-full max-w-sm rounded-2xl bg-white shadow-2xl p-5"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="text-base font-semibold text-zinc-900 mb-1">
              Delete this file?
            </div>
            <div className="text-sm text-zinc-500 mb-4 break-all">
              {pendingDelete.split("/").pop()}
              <br />
              <span className="text-xs">
                This permanently removes it from Cloudflare R2.
              </span>
            </div>
            <div className="flex gap-2 justify-end">
              <Button
                variant="outline"
                onClick={() => setPendingDelete(null)}
                className="rounded-full"
                data-testid="gallery-delete-cancel"
              >
                Cancel
              </Button>
              <Button
                onClick={() => confirmDelete(pendingDelete)}
                className="rounded-full bg-rose-600 hover:bg-rose-700 text-white"
                data-testid="gallery-delete-confirm"
              >
                <Trash2 size={14} className="mr-1.5" /> Delete
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ===========================================================================

function GalleryCard({ item, copied, onCopy, onAskDelete, onOpen, canSlider, inSlider, onToggleSlider }) {
  const bucket = bucketFor(item);
  const isImage = item.media === "image";
  const isVideo = item.media === "video";
  const badge = BADGE_BY_BUCKET[bucket];

  return (
    <button
      type="button"
      onClick={onOpen}
      className="group relative text-left rounded-xl overflow-hidden border border-zinc-200 bg-white hover:shadow-md transition-shadow"
      data-testid={`gallery-item-${item.key}`}
    >
      {/* Portrait media tile */}
      <div className="relative aspect-[3/4] bg-zinc-100 flex items-center justify-center overflow-hidden">
        {isImage ? (
          <img
            src={item.url}
            alt={item.filename}
            className="w-full h-full object-cover"
            loading="lazy"
            onError={(e) => {
              e.currentTarget.style.display = "none";
            }}
          />
        ) : isVideo ? (
          <>
            <video
              src={item.url}
              className="w-full h-full object-cover"
              muted
              playsInline
              preload="metadata"
            />
            <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
              <div className="w-12 h-12 rounded-full bg-black/55 text-white flex items-center justify-center">
                <Play size={20} className="ml-0.5" fill="currentColor" />
              </div>
            </div>
          </>
        ) : bucket === "powerpoint" ? (
          <div className="w-full h-full flex flex-col items-center justify-center text-orange-500 bg-orange-50">
            <Presentation size={42} />
            <span className="text-[10px] font-bold mt-1 tracking-wider">
              POWERPOINT
            </span>
          </div>
        ) : bucket === "voice" ? (
          <div className="w-full h-full flex flex-col items-center justify-center text-emerald-600 bg-emerald-50">
            <Mic size={42} />
            <span className="text-[10px] font-bold mt-1 tracking-wider">
              AUDIO
            </span>
          </div>
        ) : (
          <div className="w-full h-full flex flex-col items-center justify-center text-zinc-400 bg-zinc-50">
            <span className="text-3xl font-bold">
              {(item.ext || "FILE").toUpperCase()}
            </span>
          </div>
        )}

        {badge && (
          <span
            className={`absolute top-1.5 right-1.5 text-[10px] font-bold px-1.5 py-0.5 rounded text-white ${badge.cls}`}
          >
            {badge.label}
          </span>
        )}

        {canSlider && (
          <span
            role="checkbox"
            aria-checked={inSlider}
            tabIndex={0}
            onClick={onToggleSlider}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") onToggleSlider(e);
            }}
            className={`absolute bottom-1.5 left-1.5 right-1.5 flex items-center gap-1.5 px-2 py-1 rounded-md text-[10px] font-semibold cursor-pointer transition-colors ${
              inSlider
                ? "bg-emerald-600 text-white"
                : "bg-black/55 text-white hover:bg-black/70"
            }`}
            data-testid={`gallery-slider-toggle-${item.key}`}
            title="Show this on the home-page gallery (Master)"
          >
            <span
              className={`w-3.5 h-3.5 rounded-sm border flex items-center justify-center shrink-0 ${
                inSlider ? "bg-white border-white" : "border-white/80"
              }`}
            >
              {inSlider && <Check size={11} className="text-emerald-600" />}
            </span>
            {inSlider ? "On home page" : "Show on home"}
          </span>
        )}
      </div>

      {/* Filename + actions */}
      <div className="px-2 pt-2 pb-2">
        <div className="flex items-center gap-1 text-[11px] font-medium text-zinc-800">
          {bucket === "videos" && <Film size={11} className="text-rose-500 shrink-0" />}
          {bucket === "powerpoint" && (
            <Presentation size={11} className="text-orange-500 shrink-0" />
          )}
          {bucket === "voice" && <Mic size={11} className="text-emerald-600 shrink-0" />}
          {bucket === "photos" && (
            <ImageIcon size={11} className="text-zinc-500 shrink-0" />
          )}
          <span className="truncate" title={item.filename}>
            {item.filename}
          </span>
        </div>
        <div className="text-[10px] text-zinc-400 mt-0.5">
          {humanSize(item.size)}
        </div>
        <div className="mt-1.5 flex items-center gap-1">
          <button
            type="button"
            onClick={onCopy}
            className="flex-1 h-7 rounded-md bg-indigo-500 hover:bg-indigo-600 text-white text-[11px] font-semibold inline-flex items-center justify-center gap-1"
            data-testid={`gallery-copy-${item.key}`}
            title="Copy public URL"
          >
            {copied ? (
              <>
                <Check size={11} /> Copied
              </>
            ) : (
              <>
                <Copy size={11} /> Copy URL
              </>
            )}
          </button>
          {!String(item.key).startsWith("master:") && (
            <button
              type="button"
              onClick={onAskDelete}
              className="h-7 w-7 rounded-md bg-zinc-200 hover:bg-rose-100 text-zinc-700 hover:text-rose-700 inline-flex items-center justify-center"
              data-testid={`gallery-delete-${item.key}`}
              title="Delete"
            >
              <Trash2 size={11} />
            </button>
          )}
        </div>
      </div>
    </button>
  );
}

// ===========================================================================

function PreviewLightbox({ item, onClose, onCopy, copied }) {
  const isImage = item.media === "image";
  const isVideo = item.media === "video";
  const isAudio = item.media === "audio";
  return (
    <div
      className="fixed inset-0 z-50 bg-black/85 backdrop-blur-sm flex flex-col items-center justify-center px-4 py-6"
      onClick={onClose}
      data-testid="gallery-preview"
    >
      <div
        className="max-w-4xl w-full max-h-full flex flex-col items-center"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="bg-white rounded-2xl overflow-hidden w-full">
          <div className="bg-black flex items-center justify-center max-h-[70vh]">
            {isImage ? (
              <img
                src={item.url}
                alt={item.filename}
                className="max-h-[70vh] w-auto object-contain"
              />
            ) : isVideo ? (
              <video
                src={item.url}
                controls
                autoPlay
                className="max-h-[70vh] w-full"
              />
            ) : isAudio ? (
              <div className="p-10 w-full">
                <audio src={item.url} controls className="w-full" />
              </div>
            ) : (
              <div className="p-10 text-white flex flex-col items-center gap-3">
                <Presentation size={64} />
                <a
                  href={item.url}
                  target="_blank"
                  rel="noreferrer"
                  className="text-sm underline"
                >
                  Open / download {item.filename}
                </a>
              </div>
            )}
          </div>
          <div className="p-4 flex items-center justify-between gap-3 flex-wrap">
            <div className="min-w-0 flex-1">
              <div
                className="text-sm font-semibold text-zinc-900 truncate"
                title={item.filename}
              >
                {item.filename}
              </div>
              <div className="text-[11px] text-zinc-500 font-mono break-all">
                {item.url}
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Button
                onClick={onCopy}
                className="rounded-full bg-indigo-500 hover:bg-indigo-600 text-white"
                data-testid="gallery-preview-copy"
              >
                {copied ? (
                  <>
                    <Check size={14} className="mr-1.5" /> Copied
                  </>
                ) : (
                  <>
                    <Copy size={14} className="mr-1.5" /> Copy URL
                  </>
                )}
              </Button>
              <a
                href={item.url}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-1 px-3 h-9 rounded-full bg-zinc-100 hover:bg-zinc-200 text-xs font-semibold text-zinc-800"
                data-testid="gallery-preview-open"
              >
                <ExternalLink size={13} /> Open
              </a>
              <Button
                variant="outline"
                onClick={onClose}
                className="rounded-full"
                data-testid="gallery-preview-close"
              >
                Close
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
